<template>
  <div class="pm-root">
    <!-- ---------- header ---------- -->
    <div class="pm-header">
      <div>
        <h4 class="mb-0">{{ $t('partner-map.title', { year }) }}</h4>
        <p class="pm-sub mb-0">{{ $t('partner-map.subtitle') }}</p>
      </div>
      <div class="d-flex align-items-center flex-wrap" style="gap: 8px">
        <b-form-select
          v-model="year"
          :options="yearOptions"
          size="sm"
          style="width: 100px"
        />
        <b-input-group size="sm" style="width: 230px">
          <b-form-input
            v-model="query"
            :placeholder="$t('partner-map.search-placeholder')"
            debounce="250"
          />
          <b-input-group-append v-if="query">
            <b-btn @click="query = ''">&times;</b-btn>
          </b-input-group-append>
        </b-input-group>
        <b-button
          size="sm"
          variant="outline-secondary"
          :disabled="loading"
          @click="load"
        >
          {{ $t('partner-map.reload') }}
        </b-button>
        <!-- Hidden for holding contacts: the endpoint 403s them anyway, so
             offering the button would only ever produce an error. -->
        <b-button
          v-if="canShare"
          size="sm"
          variant="outline-primary"
          @click="shareOpen = true"
        >
          {{ $t('partner-map.share.button') }}
        </b-button>
      </div>
    </div>

    <partner-map-share v-model="shareOpen" />

    <b-alert v-if="error" show variant="danger">{{ error }}</b-alert>

    <div v-if="loading" class="text-center py-5">
      <b-spinner />
      <span class="ml-2">{{ $t('partner-map.loading') }}</span>
    </div>

    <template v-else-if="rawPartners.length">
      <!-- ---------- KPI tiles ---------- -->
      <div class="pm-kpis">
        <div
          v-for="t in kpis"
          :key="t.label"
          class="pm-kpi"
          :class="{ wip: t.wip }"
        >
          <div class="lbl">{{ t.label }}</div>
          <div class="val">{{ t.value }}</div>
          <div class="note">{{ t.note }}</div>
        </div>
      </div>

      <!-- ---------- status filter ---------- -->
      <b-card class="mb-3" body-class="py-3">
        <PartnerMapStatusFilter
          :statuses="allStatuses"
          :value="activeStatuses"
          :summary="statusSummary"
          :fmt="fmt"
          @toggle="toggleStatus"
          @preset="applyPreset"
        />
      </b-card>

      <!-- ---------- map + detail ---------- -->
      <b-row class="mb-3">
        <b-col lg="8" class="mb-3 mb-lg-0">
          <b-card no-body body-class="p-0">
            <div class="pm-map-controls">
              <b-form-checkbox v-model="showPartners" switch size="sm">
                {{ $t('partner-map.map.partners') }}
              </b-form-checkbox>
              <b-form-checkbox v-model="showHeroes" switch size="sm">
                {{ $t('partner-map.map.heroes') }}
              </b-form-checkbox>
              <b-form-checkbox v-model="showRest" switch size="sm">
                {{ $t('partner-map.map.rest') }}
              </b-form-checkbox>
              <b-form-checkbox v-model="showRadius" switch size="sm">
                {{ $t('partner-map.map.radius') }}
              </b-form-checkbox>
              <b-form-checkbox v-model="showLabels" switch size="sm">
                {{ $t('partner-map.map.labels') }}
              </b-form-checkbox>
              <b-form-checkbox v-model="showRatings" switch size="sm">
                {{ $t('partner-map.map.ratings') }}
              </b-form-checkbox>
              <b-form-checkbox v-model="externalOnly" switch size="sm">
                {{ $t('partner-map.map.external-only') }}
              </b-form-checkbox>
              <div class="ml-auto d-flex align-items-center" style="gap: 6px">
                <label class="mb-0 small text-muted">
                  {{ $t('partner-map.map.dot-size') }}
                </label>
                <b-form-select
                  v-model="metric"
                  :options="metricOptions"
                  size="sm"
                />
                <label class="mb-0 small text-muted">
                  {{ $t('partner-map.map.show') }}
                </label>
                <b-form-select
                  v-model="topN"
                  :options="topNOptions"
                  size="sm"
                />
              </div>
            </div>

            <PartnerMapCanvas
              :partners="visiblePartners"
              :rest-partners="restPartners"
              :heroes="heroes"
              :selection="selectionResolved"
              :metric="metric"
              :query="query"
              :show-partners="showPartners"
              :show-heroes="showHeroes"
              :show-rest="showRest"
              :show-radius="showRadius"
              :show-labels="showLabels"
              :show-ratings="showRatings"
              :partner-tooltip="partnerTooltip"
              :hero-tooltip="heroTooltip"
              :fmt="fmt"
              @select="select"
            />

            <div class="pm-legend">
              <span class="it">
                <i class="sw partner" />
                {{ $t('partner-map.legend.partner') }}
              </span>
              <span class="it">
                <i class="sw rest" />
                {{ $t('partner-map.legend.rest') }}
              </span>
              <span class="it">
                <span class="hero-scale" v-html="heroScaleSvg" />
                {{
                  $t('partner-map.legend.hero-scale', {
                    window: utilWindowLabel,
                  })
                }}
              </span>
            </div>
          </b-card>
        </b-col>

        <b-col lg="4">
          <b-card class="pm-detail-card" body-class="py-3">
            <PartnerMapDetail
              :selection="selectionResolved"
              :active-statuses="activeStatuses"
              :partners="partners"
              :heroes="heroes"
              :year="year"
              :util-window="utilWindow"
              :fmt="fmt"
            />
          </b-card>
        </b-col>
      </b-row>

      <!-- ---------- ranking ---------- -->
      <b-card class="mb-3" body-class="py-3">
        <h6 class="pm-sect">{{ $t('partner-map.bars.title', { year }) }}</h6>
        <p class="pm-legend-inline">
          <span>
            <i class="sw cost" />
            {{ $t('partner-map.bars.cost') }}
          </span>
          <span>
            <i class="sw margin" />
            {{ $t('partner-map.bars.margin') }}
          </span>
          <span>
            <i class="sw wip" />
            {{ $t('partner-map.bars.wip') }}
          </span>
          <span>
            <i class="sw over" />
            {{ $t('partner-map.bars.overrun') }}
          </span>
        </p>
        <div class="pm-bars">
          <div
            v-for="(p, i) in visiblePartners"
            :key="p.id"
            class="brow"
            :class="{ sel: isSelected(p), dim: !matches(p) }"
            @click="select({ kind: 'partner', ref: p })"
          >
            <div class="bl">
              <span class="rank">{{ i + 1 }}.</span>
              <span class="nm">{{ short(p.name) }}</span>
              <b-badge v-if="p.internal" variant="secondary">
                {{ $t('partner-map.badge.internal') }}
              </b-badge>
              <b-badge v-if="p.child_accounts" variant="light">
                {{ p.child_accounts + 1 }}
              </b-badge>
            </div>
            <div class="bt">
              <!-- Zero baseline: everything right of it is positive, left of it negative. -->
              <span class="zero" :style="{ left: barAt(0) }" />
              <!-- Cost: always positive, drawn rightward from zero. -->
              <span
                class="seg c"
                :style="{ left: barAt(0), width: barPct(p.cost_year) }"
                :title="
                  $t('partner-map.bars.cost') + ': ' + fmt.money(p.cost_year)
                "
              />
              <!-- Gain: continues rightward, so cost + margin ends at revenue. -->
              <span
                v-if="p.margin_year > 0"
                class="seg m"
                :style="{
                  left: barAt(p.cost_year),
                  width: barPct(p.margin_year),
                }"
                :title="
                  $t('partner-map.bars.margin') +
                  ': ' +
                  fmt.money(p.margin_year)
                "
              />
              <!-- Loss: subtracts, so it runs LEFT from zero into the negative gutter. -->
              <span
                v-else-if="p.margin_year < 0"
                class="seg over"
                :style="{
                  left: barAt(p.margin_year),
                  width: barPct(-p.margin_year),
                }"
                :title="
                  $t('partner-map.bars.margin') +
                  ': ' +
                  fmt.money(p.margin_year)
                "
              />
              <span
                v-if="p.revenue_wip"
                class="seg w"
                :style="{
                  left: barAt(posExtent(p)),
                  width: barPct(p.revenue_wip),
                }"
                :title="
                  $t('partner-map.kpi.revenue-wip') +
                  ': ' +
                  fmt.money(p.revenue_wip)
                "
              />
            </div>
            <div class="bv">
              {{ fmt.money(p.revenue_year) }}
              <span v-if="p.revenue_wip" class="bv-w">
                +{{ fmt.money(p.revenue_wip) }}
              </span>
              <span v-if="p.margin_year < 0" class="bv-neg">
                {{ fmt.money(p.margin_year) }}
                {{ $t('partner-map.bars.margin-label') }}
              </span>
            </div>
          </div>
        </div>
      </b-card>

      <!-- ---------- table ---------- -->
      <b-card body-class="py-3">
        <PartnerMapTable
          :tab="tab"
          :title="tableTitle"
          :partners="visiblePartners"
          :heroes="heroes"
          :selection="selectionResolved"
          :query="query"
          :fmt="fmt"
          @update:tab="tab = $event"
          @select="select"
        />
      </b-card>

      <!-- ---------- method note ---------- -->
      <b-card class="mt-3" body-class="py-3">
        <h6 class="pm-sect">{{ $t('partner-map.method.title') }}</h6>
        <ul class="pm-method">
          <li>{{ $t('partner-map.method.year-key') }}</li>
          <li>{{ $t('partner-map.method.revenue-source') }}</li>
          <li>{{ $t('partner-map.method.billed-vs-wip') }}</li>
          <li>{{ $t('partner-map.method.rollup') }}</li>
          <li>{{ $t('partner-map.method.counters') }}</li>
          <li>
            {{
              $t('partner-map.method.heroes', {
                generated: heroSnapshot.generated,
              })
            }}
          </li>
        </ul>
      </b-card>
    </template>
  </div>
</template>

<script>
import Api from '@gid/vue-common/api';
import heroSnapshot from '@/assets/mobile-heroes.json';
import PartnerMapCanvas from '@/components/partner-map/PartnerMapCanvas.vue';
import PartnerMapDetail from '@/components/partner-map/PartnerMapDetail.vue';
import PartnerMapShare from '@/components/partner-map/PartnerMapShare.vue';
import PartnerMapStatusFilter from '@/components/partner-map/PartnerMapStatusFilter.vue';
import PartnerMapTable from '@/components/partner-map/PartnerMapTable.vue';
import { mapGetters } from 'vuex';
import { isConfinedProfileSet } from '@gid/models';
import {
  GROUP_ORDER,
  HERO_R_100,
  collectStatuses,
  defaultStatuses,
  formatters,
  heroDiamond,
  heroFillR,
  heroUtilAvg,
  recompute,
  shortName,
} from '@/components/partner-map/model';

export default {
  name: 'PartnerMap',
  components: {
    PartnerMapCanvas,
    PartnerMapDetail,
    PartnerMapShare,
    PartnerMapStatusFilter,
    PartnerMapTable,
  },
  data() {
    const now = new Date().getFullYear();
    return {
      year: now,
      loading: true,
      error: null,
      shareOpen: false,
      rawPartners: [],
      heroSnapshot,
      activeStatuses: new Set(),
      // Map layers
      showPartners: true,
      showHeroes: true,
      showRest: true,
      showRadius: false,
      showLabels: false,
      showRatings: false,
      externalOnly: false,
      metric: 'revenue_all',
      topN: 30,
      query: '',
      selection: null,
      tab: 'partners',
    };
  },
  computed: {
    ...mapGetters(['contactProfiles']),
    canShare() {
      return !isConfinedProfileSet(this.contactProfiles);
    },
    fmt() {
      return formatters(this.$i18n.locale === 'de' ? 'de-DE' : 'en-GB');
    },
    yearOptions() {
      const now = new Date().getFullYear();
      return [now, now - 1, now - 2, now - 3];
    },
    metricOptions() {
      return [
        {
          value: 'revenue_all',
          text: this.$t('partner-map.metric.revenue-all'),
        },
        { value: 'revenue_year', text: this.$t('partner-map.metric.revenue') },
        { value: 'cost_year', text: this.$t('partner-map.metric.cost') },
        { value: 'margin_year', text: this.$t('partner-map.metric.margin') },
        { value: 'jobs_all', text: this.$t('partner-map.metric.jobs') },
        { value: 'uniform', text: this.$t('partner-map.metric.uniform') },
      ];
    },
    topNOptions() {
      return [10, 20, 30, 50, 100, 1000].map((n) => ({
        value: n,
        text:
          n >= this.eligiblePartners.length
            ? this.$t('partner-map.top-all')
            : `Top ${n}`,
      }));
    },
    heroes() {
      return this.heroSnapshot.heroes || [];
    },
    /**
     * `recompute()` hands back new partner objects whenever the status filter changes, so
     * the selection is held as {kind, id} and re-resolved against the current arrays.
     * Storing the object itself would leave every consumer comparing against a stale copy.
     */
    selectionResolved() {
      if (!this.selection) return null;
      const ref =
        this.selection.kind === 'partner'
          ? this.partners.find((p) => p.id === this.selection.id)
          : this.heroes.find((h) => h.name === this.selection.id);
      return ref ? { kind: this.selection.kind, ref } : null;
    },
    utilWindow() {
      return this.heroSnapshot.util_window;
    },
    utilWindowLabel() {
      const w = this.utilWindow;
      if (!w) return '';
      const d = (s) => `${s.slice(8, 10)}.${s.slice(5, 7)}.`;
      return `${d(w.from)} – ${d(w.to)}${w.to.slice(0, 4)}`;
    },
    allStatuses() {
      return collectStatuses(this.rawPartners);
    },
    /** Partner rows with headline figures derived from the current status selection. */
    partners() {
      return recompute(this.rawPartners, this.activeStatuses);
    },
    /** Every group with at least one job in the selected statuses. */
    eligiblePartners() {
      const base = this.externalOnly
        ? this.partners.filter((p) => !p.internal)
        : this.partners;
      return base.filter((p) => p.jobs_all > 0);
    },
    visiblePartners() {
      return this.eligiblePartners
        .slice()
        .sort((a, b) => b.revenue_all - a.revenue_all)
        .slice(0, this.topN);
    },
    /**
     * Groups that are not in the ranking — no jobs in the selected statuses, or below the
     * display cut-off. They stay on the map as small grey dots so the country doesn't
     * appear to empty out when the filter narrows.
     */
    restPartners() {
      const shown = new Set(this.visiblePartners);
      const base = this.externalOnly
        ? this.partners.filter((p) => !p.internal)
        : this.partners;
      return base.filter((p) => !shown.has(p) && p.lat != null);
    },
    topLabel() {
      const n = this.eligiblePartners.length;
      return this.topN >= n
        ? this.$t('partner-map.all-n', { n: this.fmt.num(n) })
        : this.$t('partner-map.top-n-of', {
            n: this.topN,
            total: this.fmt.num(n),
          });
    },
    totals() {
      const ps = this.visiblePartners;
      const sum = (f) => ps.reduce((a, p) => a + f(p), 0);
      const rated = ps.filter((p) => p.rating_avg != null);
      const ratingCount = rated.reduce((a, p) => a + p.rating_count, 0);
      return {
        revenue: sum((p) => p.revenue_year),
        cost: sum((p) => p.cost_year),
        jobs: sum((p) => p.jobs_year),
        revenueWip: sum((p) => p.revenue_wip),
        costWip: sum((p) => p.cost_wip),
        jobsWip: sum((p) => p.jobs_wip),
        ratingCount,
        rating: ratingCount
          ? rated.reduce((a, p) => a + p.rating_avg * p.rating_count, 0) /
            ratingCount
          : null,
      };
    },
    kpis() {
      const t = this.totals;
      const margin = t.revenue ? ((t.revenue - t.cost) / t.revenue) * 100 : 0;
      return [
        {
          label: this.$t('partner-map.kpi.revenue', { year: this.year }),
          value: this.fmt.money(t.revenue),
          note: this.$t('partner-map.kpi.revenue-note', {
            n: this.fmt.num(t.jobs),
            scope: this.topLabel,
          }),
        },
        {
          label: this.$t('partner-map.kpi.cost', { year: this.year }),
          value: this.fmt.money(t.cost),
          note: this.$t('partner-map.kpi.cost-note'),
        },
        {
          label: this.$t('partner-map.kpi.margin', { year: this.year }),
          value: this.fmt.money(t.revenue - t.cost),
          note: this.$t('partner-map.kpi.margin-note', {
            pct: this.fmt.pct(margin),
          }),
        },
        {
          label: this.$t('partner-map.kpi.revenue-wip'),
          value: this.fmt.money(t.revenueWip),
          note: this.$t('partner-map.kpi.revenue-wip-note', {
            n: this.fmt.num(t.jobsWip),
          }),
          wip: true,
        },
        {
          label: this.$t('partner-map.kpi.cost-wip'),
          value: this.fmt.money(t.costWip),
          note: this.$t('partner-map.kpi.cost-wip-note'),
          wip: true,
        },
        {
          label: this.$t('partner-map.kpi.jobs-in-filter'),
          value: this.fmt.num(t.jobs + t.jobsWip),
          note: this.$t('partner-map.kpi.jobs-in-filter-note', {
            n: this.activeStatuses.size,
            total: this.allStatuses.length,
          }),
        },
        {
          label: this.$t('partner-map.kpi.rating'),
          value: t.rating == null ? '–' : this.fmt.dec(t.rating),
          note: this.$t('partner-map.kpi.rating-note', {
            n: this.fmt.num(t.ratingCount),
          }),
        },
        {
          label: this.$t('partner-map.kpi.heroes'),
          value: this.fmt.num(this.heroes.length),
          note: this.$t('partner-map.kpi.heroes-note', {
            pct: this.fmt.pctOfOne(heroUtilAvg(this.heroes)),
            window: this.utilWindowLabel,
          }),
        },
      ];
    },
    statusSummary() {
      const selected = this.allStatuses.filter((s) =>
        this.activeStatuses.has(s.key),
      );
      if (!selected.length) return this.$t('partner-map.status.none-selected');
      const byGroup = {};
      selected.forEach((s) => {
        byGroup[s.group] = (byGroup[s.group] || 0) + 1;
      });
      const parts = GROUP_ORDER.filter((g) => byGroup[g]).map(
        (g) => `${this.$t(`partner-map.group.${g}`)} (${byGroup[g]})`,
      );
      const isDefault =
        selected.length === this.defaultStatusKeys.length &&
        this.defaultStatusKeys.every((k) => this.activeStatuses.has(k));
      return (
        (isDefault
          ? `<b>${this.$t('partner-map.status.is-default')}</b> `
          : '') +
        this.$t('partner-map.status.summary', {
          n: selected.length,
          total: this.allStatuses.length,
          groups: parts.join(' · '),
          eligible: this.fmt.num(this.eligiblePartners.length),
          all: this.fmt.num(this.partners.length),
          scope: this.topLabel,
          jobs: this.fmt.num(this.totals.jobs),
          revenue: this.fmt.money(this.totals.revenue),
        })
      );
    },
    defaultStatusKeys() {
      return defaultStatuses(this.allStatuses);
    },
    tableTitle() {
      return this.tab === 'partners'
        ? this.$t('partner-map.table.partners-title', {
            year: this.year,
            scope: this.topLabel,
          })
        : this.$t('partner-map.table.heroes-title');
    },
    maxBar() {
      return Math.max(...this.visiblePartners.map((p) => p.revenue_all), 1);
    },
    /**
     * Bar scale for the ranking, as a diverging axis around a zero baseline.
     *
     * revenue = cost + margin, so cost is always a positive quantity drawn rightward. A
     * NEGATIVE margin is drawn leftward of zero, because it subtracts: appending it to the
     * right would make a loss-making partner render as a longer bar, i.e. read as bigger
     * rather than as underwater. `neg` reserves the left gutter; it collapses to 0 (and the
     * chart back to a plain stacked bar) when every partner is profitable.
     */
    barScale() {
      const rows = this.visiblePartners;
      const pos = Math.max(
        ...rows.map(
          (p) => p.cost_year + Math.max(0, p.margin_year) + p.revenue_wip,
        ),
        1,
      );
      const neg = Math.max(...rows.map((p) => Math.max(0, -p.margin_year)), 0);
      return { pos, neg, span: pos + neg || 1 };
    },
    /** Legend: five reference gauges across the utilization scale. */
    heroScaleSvg() {
      const points = [0.25, 0.5, 0.75, 1.0, 1.2];
      const step = 2 * HERO_R_100 + 8;
      const w = Math.round(step * points.length);
      const h = 2 * HERO_R_100 + 16;
      const cy = HERO_R_100 + 2;
      const body = points
        .map((u, i) => {
          const cx = Math.round(step * (i + 0.5));
          return (
            `<path d="${heroDiamond(
              cx,
              cy,
              HERO_R_100,
            )}" class="pm-hero-frame"/>` +
            `<path d="${heroDiamond(
              cx,
              cy,
              heroFillR(u),
            )}" class="pm-hero-fill${u > 1 ? ' over' : ''}"/>` +
            `<text x="${cx}" y="${
              h - 1
            }" text-anchor="middle" font-size="8.5">${Math.round(
              u * 100,
            )}%</text>`
          );
        })
        .join('');
      return `<svg width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">${body}</svg>`;
    },
  },
  watch: {
    year() {
      this.load();
    },
  },
  created() {
    this.load();
  },
  methods: {
    short: shortName,
    async load() {
      this.loading = true;
      this.error = null;
      try {
        const { data } = await Api.get('/api/admin/partners/map', {
          params: { year: this.year },
        });
        this.rawPartners = data.partners || [];
        // Keep the selection only if the same group still exists in the new year.
        this.selection = null;
        this.activeStatuses = new Set(
          defaultStatuses(collectStatuses(this.rawPartners)),
        );
      } catch (e) {
        this.error = e.response?.data?.message || e.message;
        this.rawPartners = [];
      } finally {
        this.loading = false;
      }
    },
    select(sel) {
      this.selection = sel
        ? {
            kind: sel.kind,
            id: sel.kind === 'partner' ? sel.ref.id : sel.ref.name,
          }
        : null;
    },
    isSelected(o) {
      const sel = this.selectionResolved;
      return !!sel && sel.ref === o;
    },
    toggleStatus(key) {
      const next = new Set(this.activeStatuses);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      this.activeStatuses = next;
    },
    applyPreset(preset) {
      if (preset === 'all') {
        this.activeStatuses = new Set(this.allStatuses.map((s) => s.key));
      } else if (preset === 'default') {
        this.activeStatuses = new Set(this.defaultStatusKeys);
      } else {
        this.activeStatuses = new Set(
          this.allStatuses.filter((s) => s.group === preset).map((s) => s.key),
        );
      }
    },
    matches(o) {
      if (!this.query) return true;
      const q = this.query.toLowerCase();
      return (
        String(o.name || '')
          .toLowerCase()
          .includes(q) ||
        String(o.city || '')
          .toLowerCase()
          .includes(q)
      );
    },
    /** Width of `v` on the diverging bar scale. */
    barPct(v) {
      return `${(Math.max(0, v || 0) / this.barScale.span) * 100}%`;
    },
    /** Left edge at `v` units right of the zero baseline (negative v moves left). */
    barAt(v) {
      return `${((this.barScale.neg + v) / this.barScale.span) * 100}%`;
    },
    /** Positive extent of a row: cost, plus margin only while it is a gain. */
    posExtent(p) {
      return p.cost_year + Math.max(0, p.margin_year);
    },
    esc(s) {
      return String(s == null ? '' : s).replace(
        /[&<>"]/g,
        (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]),
      );
    },
    partnerTooltip(p) {
      const note =
        p.jobs_all === 0
          ? `<div class="t-note">${this.esc(
              this.$t('partner-map.tip.no-jobs'),
            )}</div>`
          : '';
      const rows = [
        [
          this.$t('partner-map.kpi.revenue', { year: this.year }),
          this.fmt.money(p.revenue_year),
        ],
        [
          this.$t('partner-map.kpi.cost', { year: this.year }),
          this.fmt.money(p.cost_year),
        ],
        [this.$t('partner-map.kpi.revenue-wip'), this.fmt.money(p.revenue_wip)],
        [
          this.$t('partner-map.tip.jobs-billed-wip'),
          `${this.fmt.num(p.jobs_year)} / ${this.fmt.num(p.jobs_wip)}`,
        ],
        [
          this.$t('partner-map.detail.active-jobs'),
          this.fmt.num(p.active_jobs_now),
        ],
        [this.$t('partner-map.detail.rating-avg'), this.fmt.dec(p.rating_avg)],
        [
          this.$t('partner-map.detail.service-radius'),
          `${this.fmt.num(p.service_area_km)} km`,
        ],
      ];
      return (
        `<div class="t">${this.esc(p.name)}</div>${note}<dl>` +
        rows
          .map(([k, v]) => `<dt>${this.esc(k)}</dt><dd>${this.esc(v)}</dd>`)
          .join('') +
        '</dl>'
      );
    },
    heroTooltip(h) {
      const rows = [
        [this.$t('partner-map.tip.location'), `${h.postal_code} ${h.city}`],
        [
          this.$t('partner-map.detail.service-radius'),
          `${this.fmt.num(h.radius_km)} km`,
        ],
        [
          this.$t('partner-map.detail.util-6m'),
          h.util
            ? this.fmt.pctOfOne(h.util.util)
            : this.$t('partner-map.detail.no-util-data-short'),
        ],
        [
          this.$t('partner-map.tip.assignments'),
          h.util ? this.fmt.num(h.util.jobs) : '–',
        ],
        [
          this.$t('partner-map.detail.gid-account'),
          h.gid_account || this.$t('partner-map.detail.not-linked'),
        ],
      ];
      return (
        `<div class="t">${this.esc(h.name)}</div><dl>` +
        rows
          .map(([k, v]) => `<dt>${this.esc(k)}</dt><dd>${this.esc(v)}</dd>`)
          .join('') +
        '</dl>'
      );
    },
  },
};
</script>

<style lang="scss">
.pm-root {
  // Scoped design tokens. The categorical hues are the validated data-viz slots 1–3;
  // status colours are deliberately distinct so a series colour never impersonates a state.
  --pm-partner: #2a78d6;
  --pm-partner-wash: #eaf2fd;
  --pm-hero: #eb6834;
  --pm-margin: #1baf7a;
  --pm-warn: #eda100;
  --pm-crit: #d03b3b;
  --pm-ink-1: #1c1c1c;
  --pm-ink-2: #52514e;
  --pm-ink-3: #898781;
  --pm-border: #e3e3df;
  --pm-surface-1: #ffffff;
  --pm-surface-2: #f5f5f2;

  padding-bottom: 24px;
}
</style>

<style lang="scss" scoped>
.pm-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin: 16px 0 12px;
}

.pm-sub {
  font-size: 12.5px;
  color: var(--pm-ink-3);
}

.pm-kpis {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(165px, 1fr));
  gap: 8px;
  margin-bottom: 12px;
}

.pm-kpi {
  background: var(--pm-surface-1);
  border: 1px solid var(--pm-border);
  border-radius: 6px;
  padding: 9px 11px;

  .lbl {
    font-size: 11px;
    color: var(--pm-ink-3);
  }
  .val {
    font-size: 20px;
    font-weight: 700;
    line-height: 1.2;
  }
  .note {
    font-size: 11px;
    color: var(--pm-ink-3);
  }
  // Backlog tiles are dashed so they can never be mistaken for booked money.
  &.wip {
    border-style: dashed;
    background: transparent;
  }
}

.pm-map-controls {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px 16px;
  padding: 9px 12px;
  border-bottom: 1px solid var(--pm-border);
  font-size: 12.5px;
}

.pm-legend {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 18px;
  padding: 8px 12px;
  border-top: 1px solid var(--pm-border);
  font-size: 11.5px;
  color: var(--pm-ink-2);

  .it {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .sw {
    width: 11px;
    height: 11px;
    border-radius: 50%;
    display: inline-block;
  }
  .sw.partner {
    background: var(--pm-partner);
  }
  .sw.rest {
    background: var(--pm-ink-3);
    width: 7px;
    height: 7px;
  }
}

.pm-legend-inline {
  display: flex;
  gap: 14px;
  font-size: 11.5px;
  color: var(--pm-ink-2);
  margin-bottom: 8px;

  .sw {
    display: inline-block;
    width: 11px;
    height: 11px;
    border-radius: 2px;
    margin-right: 5px;
    vertical-align: -1px;
  }
  .sw.cost {
    background: var(--pm-partner);
  }
  .sw.margin {
    background: var(--pm-margin);
  }
  .sw.wip {
    background: repeating-linear-gradient(
      45deg,
      var(--pm-partner) 0 2px,
      transparent 2px 4px
    );
    border: 1px solid var(--pm-border);
  }
  .sw.over {
    background: var(--pm-crit);
  }
}

.pm-sect {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--pm-ink-3);
  margin-bottom: 6px;
}

.pm-bars {
  .brow {
    display: grid;
    // The value column is FIXED, not auto: a longer label (a negative margin adds
    // "−1.270 € Marge") would otherwise steal width from that row's bar alone,
    // giving it its own scale and shifting its zero baseline off the others.
    grid-template-columns: minmax(150px, 210px) 1fr 190px;
    align-items: center;
    gap: 10px;
    padding: 2px 4px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12.5px;
  }
  .brow:hover {
    background: var(--pm-surface-2);
  }
  .brow.sel {
    background: var(--pm-partner-wash);
  }
  .brow.dim {
    opacity: 0.25;
  }

  .bl {
    display: flex;
    align-items: center;
    gap: 5px;
    min-width: 0;
  }
  .nm {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .rank {
    color: var(--pm-ink-3);
    font-variant-numeric: tabular-nums;
    flex: 0 0 auto;
  }
  .bl .badge {
    flex: 0 0 auto;
  }

  .bt {
    // Segments are absolutely placed against a shared zero baseline, so a negative margin
    // can sit left of it. Overflow stays visible: the gutter is sized from the data.
    position: relative;
    height: 13px;
    background: var(--pm-surface-2);
    border-radius: 3px;
  }
  .zero {
    position: absolute;
    top: -2px;
    bottom: -2px;
    width: 1px;
    background: var(--pm-ink-3);
    opacity: 0.55;
  }
  .seg {
    position: absolute;
    top: 0;
    bottom: 0;
  }
  .seg.c {
    background: var(--pm-partner);
  }
  .seg.m {
    background: var(--pm-margin);
  }
  .seg.w {
    background: repeating-linear-gradient(
      45deg,
      var(--pm-partner) 0 2px,
      transparent 2px 4px
    );
    opacity: 0.6;
  }
  // Cost paid out beyond what was billed.
  .seg.over {
    background: var(--pm-crit);
  }

  .bv {
    text-align: right;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
  .bv-w {
    color: var(--pm-ink-3);
  }
  .bv-neg {
    color: var(--pm-crit);
  }
}

.pm-detail-card {
  max-height: min(72vh, 780px);
  overflow-y: auto;
}

.pm-method {
  font-size: 12px;
  color: var(--pm-ink-2);
  margin: 0;
  padding-left: 18px;

  li {
    margin-bottom: 3px;
  }
}

.hero-scale ::v-deep svg {
  vertical-align: middle;
}
.hero-scale ::v-deep .pm-hero-frame {
  fill: var(--pm-hero);
  fill-opacity: 0.1;
  stroke: var(--pm-hero);
  stroke-width: 1.2;
  stroke-opacity: 0.75;
}
.hero-scale ::v-deep .pm-hero-fill {
  fill: var(--pm-hero);
  stroke: #fff;
  stroke-width: 0.8;
}
.hero-scale ::v-deep .pm-hero-fill.over {
  fill: var(--pm-crit);
}
.hero-scale ::v-deep text {
  fill: var(--pm-ink-3);
}
</style>
